import { t } from '@lingui/macro'
import { getTimezone } from 'countries-and-timezones'
import { useRouter } from 'next/router'

import { Avatar, Button, Heading, Modal, Space, Spinner, Text } from '@/components'
import {
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconLinkedin,
  IconMapPin,
  IconMessage,
  IconStar,
  IconTrash,
} from '@/components/Icons'
import { Events, Routes } from '@/constants'
import { useCohortContext } from '@/contexts/CohortContext'
import { useCurrentUserContext } from '@/contexts/CurrentUserContext'
import { useLearningCommunityContext } from '@/contexts/LearningCommunityContext'
import { PronounsEnum } from '@/graphql/generated'
import { getPronounsLabel } from '@/helpers/userProfile/getPronounsLabel'
import useCommunityProfile from '@/hooks/useCommunityProfile'
import useDirectMessages from '@/hooks/useDirectMessages'
import usePermissions from '@/hooks/usePermissions'
import useTracking from '@/hooks/useTracking'
import { formatDate } from '@/utils/date/'
import { isUrl, withProtocol } from '@/utils/url'

import ProfileDetailItem from './ProfileDetailItem'

type Props = {
  getPreviousUserId?: () => string | undefined
  getNextUserId?: () => string | undefined
  userId: string
  onClose: () => void
}

const isLinkedInUrl = (url: string): boolean => url.toLowerCase().includes('linkedin')

const ProfileModal: React.FC<React.PropsWithChildren<Props>> = ({
  onClose,
  userId,
  getNextUserId,
  getPreviousUserId,
}) => {
  const { query, replace, pathname } = useRouter()
  const { hasLearningAccess } = usePermissions()
  const cohortContext = useCohortContext()
  const currentUserContext = useCurrentUserContext()
  const learningCommunityContext = useLearningCommunityContext()
  const { trackEvent, withCurrentLearningCommunity, withCurrentCohortId } = useTracking()
  const { openChannel } = useDirectMessages()

  const { userProfile, loading, mutationRunning, addFavoritePeer, removeFavoritePeer } = useCommunityProfile(
    userId,
    cohortContext?.cohort?.id,
  )

  const track = (eventName: string, additionalFields = {}): void => {
    trackEvent(eventName, withCurrentLearningCommunity(), withCurrentCohortId(), additionalFields)
  }

  const toggleFavorite = (): void => {
    if (!cohortContext?.cohort?.id || !userProfile) return

    const payload = {
      variables: {
        cohortId: cohortContext.cohort.id,
        userId: userId,
      },
    }

    userProfile?.cohortProfile?.viewer.favorite ? removeFavoritePeer(payload) : addFavoritePeer(payload)
  }

  const onNext = (): void => {
    const nextUserId = getNextUserId?.()
    if (!nextUserId) return

    track(Events.CLICKED_TO_VIEW_NEXT_PROFILE)
    replace({ pathname, query: { ...query, selectedUserId: nextUserId } }, undefined, { scroll: false })
  }

  const onPrevious = (): void => {
    const previousUserId = getPreviousUserId?.()
    if (!previousUserId) return

    track(Events.CLICKED_TO_VIEW_PREVIOUS_PROFILE)
    replace({ pathname, query: { ...query, selectedUserId: previousUserId } }, undefined, { scroll: false })
  }

  const onMessageClick = (): void => {
    track(Events.CLICKED_MESSAGE_ICON, { location: 'Profile Modal' })

    openChannel?.([{ id: userId }], cohortContext?.cohort)
  }

  const isCurrentUserProfile = currentUserContext?.currentUser?.me?.id === userId
  const isLinkedIn = isLinkedInUrl(userProfile?.professionalUrl ?? '')

  return (
    <Modal screenHeight opened onClose={onClose} showClose size="xlarge">
      <div className="flex h-full flex-col overflow-y-auto p-9">
        {loading || !userProfile ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : (
          <>
            <div className="items-center justify-between gap-9 space-y-6 md:flex md:space-y-0">
              <div className="min-w-32 flex-initial">
                <Avatar name={userProfile?.fullName ?? ''} size="4xl" url={userProfile.avatarUrl} />
              </div>
              <div className="grow space-y-2">
                <div>
                  <div className="flex items-center space-x-2">
                    <Heading level={3} variant={TextStyle => TextStyle.MEDIUM_XL_NORMAL}>
                      {userProfile?.fullName}
                    </Heading>

                    {userProfile?.pronouns && userProfile.pronouns !== PronounsEnum.RatherNotSay ? (
                      <span className="text-grey-dark text-sm">{getPronounsLabel(userProfile.pronouns)}</span>
                    ) : null}
                  </div>
                  <div className="md:max-w-24 truncate text-grey-dark empty:h-7 xl:max-w-[400px]">
                    {userProfile?.jobDescription}
                  </div>
                </div>
                <div>
                  {userProfile?.location ? <ProfileDetailItem icon={IconMapPin} value={userProfile.location} /> : null}
                  {userProfile?.timezone ? (
                    <ProfileDetailItem
                      icon={IconClock}
                      value={`${formatDate(
                        null,
                        { hour: 'numeric', minute: 'numeric', timeZone: userProfile.timezone ?? undefined },
                        'en-US',
                      )} (GMT${getTimezone(userProfile.timezone)?.utcOffsetStr})`}
                    />
                  ) : null}

                  {userProfile?.professionalUrl ? (
                    <ProfileDetailItem
                      icon={isLinkedIn ? IconLinkedin : undefined}
                      link={
                        isUrl(withProtocol(userProfile.professionalUrl))
                          ? withProtocol(userProfile.professionalUrl)
                          : undefined
                      }
                      value={isLinkedIn ? 'LinkedIn' : userProfile.professionalUrl}
                    />
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col gap-4 md:flex-row">
                {hasLearningAccess && isCurrentUserProfile ? (
                  <Button href={`/${query?.slug}/${Routes.LEARN}/${query?.cohortId}/${Routes.PROFILE}`} type="link">
                    {t`Edit profile`}
                  </Button>
                ) : null}

                {!isCurrentUserProfile && hasLearningAccess ? (
                  <Button
                    disabled={mutationRunning}
                    onClick={toggleFavorite}
                    align="center"
                    icon={userProfile?.cohortProfile?.viewer.favorite ? IconTrash : IconStar}
                    variant="secondary"
                  >
                    {userProfile?.cohortProfile?.viewer.favorite ? t`Remove bookmark` : t`Bookmark this profile`}
                  </Button>
                ) : null}

                {!isCurrentUserProfile ? (
                  <Button variant="primary" icon={IconMessage} onClick={onMessageClick}>
                    {t`Message`}
                  </Button>
                ) : null}

                {getPreviousUserId && getNextUserId ? (
                  <div className="space-x-2">
                    <Button onClick={onPrevious} aria-label="previous" icon={IconChevronLeft} />

                    <Button onClick={onNext} aria-label="next" icon={IconChevronRight} />
                  </div>
                ) : null}
              </div>
            </div>
            <Space y={8} />
            <div className="h-full justify-between space-y-4">
              <div className="space-y-4">
                {userProfile?.cohortProfile?.enrollmentReason ? (
                  <ProfileAnswerItem
                    title={t`I am taking the ${learningCommunityContext?.learningCommunity?.name} to`}
                    answer={userProfile.cohortProfile.enrollmentReason}
                  />
                ) : null}

                {userProfile?.cohortProfile?.canHelpWith ? (
                  <ProfileAnswerItem
                    title={t`I can help this community with`}
                    answer={userProfile.cohortProfile.canHelpWith}
                  />
                ) : null}

                {userProfile?.cohortProfile?.canBeHelpedWith ? (
                  <ProfileAnswerItem title={t`They can help me`} answer={userProfile.cohortProfile.canBeHelpedWith} />
                ) : null}

                {userProfile?.aboutMe ? (
                  <ProfileAnswerItem title={t`Here's some more information about me`} answer={userProfile.aboutMe} />
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default ProfileModal

const ProfileAnswerItem: React.FC<{ title: string; answer: string }> = ({ title, answer }) => (
  <div className="space-y-2 rounded-md bg-grey-bright p-4 ">
    <Text variant={TextStyle => TextStyle.MEDIUM_LG_NORMAL} className="text-grey-dark">
      {title}:
    </Text>
    <Text variant={TextStyle => TextStyle.REGULAR_BASE_NORMAL} className="whitespace-pre-line text-navy-dark">
      {answer}
    </Text>
  </div>
)
